import React, { memo, type ReactNode, type Ref, useCallback, useMemo } from "react";
import { Calendar, DateField, DatePicker, Label } from "@heroui/react";
import { type CalendarDate, parseDate } from "@internationalized/date";

type TISOProps = {
    onChange?: (v: string | undefined) => void;
    value?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    label?: ReactNode;
    minValue?: CalendarDate;
    maxValue?: CalendarDate;
    "data-testid"?: string;
};

/**
 * DateInput that works with ISO format "YYYY-MM-DD" strings.
 * CalendarDate.toString() produces this format.
 */
export const DateInput = memo(
    ({
        ref,
        value,
        onChange,
        isDisabled,
        isRequired,
        label,
        minValue,
        maxValue,
        "data-testid": dataTestId,
    }: TISOProps & {
        ref?: Ref<HTMLDivElement>;
    }) => {
        const calendarValue = useMemo(() => {
            if (!value) {
                return null;
            }
            try {
                return parseDate(value);
            } catch {
                return null;
            }
        }, [value]);

        const handleOnChange = useCallback(
            (date: CalendarDate | null): void => {
                onChange?.(date?.toString());
            },
            [onChange],
        );

        return (
            <DatePicker
                ref={ref}
                aria-label="Date"
                data-testid={dataTestId}
                shouldForceLeadingZeros
                value={calendarValue}
                onChange={handleOnChange}
                isDisabled={isDisabled}
                minValue={minValue}
                maxValue={maxValue}
                className="w-36"
                granularity="day"
            >
                {label && <Label isRequired={isRequired}>{label}</Label>}
                <DateField.Group fullWidth>
                    <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
                    <DateField.Suffix>
                        <DatePicker.Trigger>
                            <DatePicker.TriggerIndicator />
                        </DatePicker.Trigger>
                    </DateField.Suffix>
                </DateField.Group>
                <DatePicker.Popover className="max-w-none">
                    <Calendar aria-label="Date">
                        <Calendar.Header>
                            <Calendar.YearPickerTrigger>
                                <Calendar.YearPickerTriggerHeading />
                                <Calendar.YearPickerTriggerIndicator />
                            </Calendar.YearPickerTrigger>
                            <Calendar.NavButton slot="previous" />
                            <Calendar.NavButton slot="next" />
                        </Calendar.Header>
                        <Calendar.Grid>
                            <Calendar.GridHeader>
                                {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
                            </Calendar.GridHeader>
                            <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
                        </Calendar.Grid>
                        <Calendar.YearPickerGrid>
                            <Calendar.YearPickerGridBody>
                                {({ year }) => <Calendar.YearPickerCell year={year} />}
                            </Calendar.YearPickerGridBody>
                        </Calendar.YearPickerGrid>
                    </Calendar>
                </DatePicker.Popover>
            </DatePicker>
        );
    },
);
